import { ExternalLink } from "lucide-react";

interface ExternalResourcesProps {
  areaName?: string;
  showCaseStudies?: boolean;
  showTechnical?: boolean;
  showBenefits?: boolean;
}

export const ExternalResources = ({ 
  areaName, 
  showCaseStudies = true, 
  showTechnical = true, 
  showBenefits = true 
}: ExternalResourcesProps) => {
  
  const caseStudyLinks = [
    {
      title: "Case Study: Secondary Glazing in Chiswick Homes",
      url: "https://secondarydoubleglazing.co.uk/exploring-the-benefits-of-secondary-glazing-a-case-study-in-chiswick-homes/",
      description: "Exploring the benefits and real-world results"
    },
    {
      title: "Energy Efficiency Impact in Barnes",
      url: "https://secondarydoubleglazing.co.uk/case-study-the-impact-of-secondary-glazing-on-energy-efficiency-and-noise-reduction-in-barnes-homes/",
      description: "Detailed analysis of energy savings and noise reduction"
    },
    {
      title: "Wimbledon Secondary Glazing Results",
      url: "https://secondarydoubleglazing.co.uk/case-study-the-impact-of-secondary-glazing-on-energy-efficiency-and-noise-reduction-in-wimbledon-homes/",
      description: "Comprehensive study on performance improvements"
    },
    {
      title: "Putney Energy Efficiency Enhancement",
      url: "https://secondarydoubleglazing.co.uk/case-study-enhancing-energy-efficiency-and-sound-insulation-through-secondary-glazing-in-putney-homes/",
      description: "Sound insulation and energy efficiency results"
    },
    {
      title: "St John's Wood Installation Study",
      url: "https://secondarydoubleglazing.co.uk/case-study-enhancing-sound-insulation-and-energy-efficiency-through-secondary-glazing-in-st-johns-wood-homes/",
      description: "Premium area installation case study"
    },
    {
      title: "Hampstead Secondary Glazing Benefits",
      url: "https://secondarydoubleglazing.co.uk/case-study-enhancing-energy-efficiency-and-noise-reduction-with-secondary-double-glazing-in-hampstead-homes/",
      description: "Heritage property solutions and benefits"
    }
  ];

  const technicalGuides = [
    {
      title: "Secondary Glazing Installation Guide",
      url: "https://secondarydoubleglazing.co.uk/step-by-step-guide-to-installing-secondary-glazing-for-drafty-windows-enhance-your-homes-energy-efficiency-and-comfort/",
      description: "Complete step-by-step installation process"
    },
    {
      title: "Window Insulation Techniques",
      url: "https://secondarydoubleglazing.co.uk/effective-methods-for-insulating-windows-for-winter-a-comprehensive-guide-to-secondary-glazing/",
      description: "Professional insulation methods and techniques"
    },
    {
      title: "Secondary vs Triple Glazing Comparison",
      url: "https://secondarydoubleglazing.co.uk/comparing-efficiency-and-cost-an-in-depth-analysis-of-secondary-glazing-and-triple-glazing-as-the-new-industry-standard/",
      description: "Detailed efficiency and cost analysis"
    },
    {
      title: "Vacuum Glazing Technology",
      url: "https://secondarydoubleglazing.co.uk/exploring-vacuum-glazing-how-this-innovative-technology-is-revolutionizing-thermal-efficiency-in-modern-architecture/",
      description: "Advanced glazing technology insights"
    }
  ];

  const benefitResources = [
    {
      title: "Secondary Glazing Benefits Overview",
      url: "https://secondarydoubleglazing.co.uk/secondary-glazing-benefits/",
      description: "Comprehensive benefits analysis"
    },
    {
      title: "Noise Reduction Effectiveness",
      url: "https://secondarydoubleglazing.co.uk/does-secondary-glazing-reduce-noise/",
      description: "Scientific analysis of noise reduction capabilities"
    },
    {
      title: "Energy Saving Performance",
      url: "https://secondarydoubleglazing.co.uk/secondary-glazing-energy-saving/",
      description: "Detailed energy efficiency benefits"
    },
    {
      title: "Heritage Building Solutions",
      url: "https://secondarydoubleglazing.co.uk/exploring-the-benefits-of-secondary-glazing-for-heritage-and-listed-buildings-preserving-character-while-improving-energy-efficiency/",
      description: "Specialized solutions for listed properties"
    }
  ];

  return (
    <div className="bg-blue-50 p-6 rounded-lg my-8 border border-blue-200">
      <h3 className="text-xl font-semibold mb-6 text-primary flex items-center">
        <ExternalLink className="mr-2 h-5 w-5" />
        Further Reading & Resources
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {showCaseStudies && (
          <div>
            <h4 className="font-medium mb-3 text-secondary-dark">Case Studies</h4>
            <ul className="space-y-3">
              {caseStudyLinks.slice(0, 4).map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.url} 
                    target="_blank" 
                    rel="nofollow noopener noreferrer"
                    className="text-blue-700 hover:text-blue-900 hover:underline transition-colors block"
                  >
                    <div className="text-sm font-medium">{link.title}</div>
                    <div className="text-xs text-gray-600 mt-1">{link.description}</div>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {showTechnical && (
          <div>
            <h4 className="font-medium mb-3 text-secondary-dark">Technical Guides</h4>
            <ul className="space-y-3">
              {technicalGuides.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.url} 
                    target="_blank" 
                    rel="nofollow noopener noreferrer"
                    className="text-blue-700 hover:text-blue-900 hover:underline transition-colors block"
                  >
                    <div className="text-sm font-medium">{link.title}</div>
                    <div className="text-xs text-gray-600 mt-1">{link.description}</div>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {showBenefits && (
          <div>
            <h4 className="font-medium mb-3 text-secondary-dark">Benefits & Performance</h4>
            <ul className="space-y-3">
              {benefitResources.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.url} 
                    target="_blank" 
                    rel="nofollow noopener noreferrer"
                    className="text-blue-700 hover:text-blue-900 hover:underline transition-colors block"
                  >
                    <div className="text-sm font-medium">{link.title}</div>
                    <div className="text-xs text-gray-600 mt-1">{link.description}</div>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      
      <div className="mt-4 pt-4 border-t border-blue-300">
        <p className="text-xs text-gray-600 flex items-center">
          <ExternalLink className="mr-1 h-3 w-3" />
          Further reading from our sister site, opens in a new window
        </p>
      </div>
    </div>
  );
};